import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const ApolloVsNoQ5 = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Apollo.io vs NoQ5 — AI CRM Agent Comparison 2026",
      "description": "Detailed comparison of Apollo.io and NoQ5 for CRM automation, data enrichment, and sales intelligence.",
      "url": "https://noq5.ai/apollo-vs-noq5",
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Apollo.io vs NoQ5 — AI CRM Agent Comparison (2026) | NoQ5"
        description="Apollo.io vs NoQ5: Compare features, pricing, and CRM automation capabilities. See why teams switch from Apollo to NoQ5 for autonomous CRM execution."
        keywords="Apollo vs NoQ5, Apollo.io alternative, Apollo.io comparison, Apollo CRM alternative, Apollo AI alternative, best Apollo alternative"
        canonicalUrl="https://noq5.ai/apollo-vs-noq5"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">COMPARISON</span>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">Apollo.io vs NoQ5</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Apollo.io gives you a database and outreach tools. NoQ5 gives you an AI agent that runs your entire CRM autonomously. Different tools, different categories.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-card rounded-xl border border-border">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left font-bold">Feature</th>
                  <th className="p-4 text-center font-bold">Apollo.io</th>
                  <th className="p-4 text-center font-bold text-primary">NoQ5</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Contact database", "275M+ contacts", "Enrichment from 50+ sources", true, true],
                  ["Email sequences", "Built-in", "AI-generated & autonomous", true, true],
                  ["CRM data entry automation", "No", "Fully autonomous", false, true],
                  ["Meeting → CRM updates", "No", "Automatic", false, true],
                  ["AI pipeline management", "No", "Evidence-based", false, true],
                  ["Revenue forecasting", "No", "AI-powered", false, true],
                  ["Autonomous CRM agent", "No", "Core product", false, true],
                  ["Multi-CRM support", "Limited", "Salesforce, HubSpot, Pipedrive, Zoho", false, true],
                  ["GDPR compliance (EU)", "US-based", "EU-first, GDPR native", false, true],
                  ["Pricing model", "Per seat + credits", "Flat rate", true, true]
                ].map(([feature, apollo, noq5, apolloHas, noq5Has], i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{feature as string}</td>
                    <td className="p-4 text-center text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        {apolloHas ? <CheckCircle className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-destructive" />}
                        <span className="text-sm">{apollo as string}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {noq5Has ? <CheckCircle className="h-4 w-4 text-primary" /> : <X className="h-4 w-4 text-destructive" />}
                        <span className="text-sm font-medium text-primary">{noq5 as string}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Key Difference */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-8">The Fundamental Difference</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-card p-8 rounded-xl border border-border">
              <h3 className="text-xl font-bold mb-4">Apollo.io = Database + Outreach</h3>
              <p className="text-muted-foreground">Apollo gives you a contact database and email sequences. It's great for prospecting — but it doesn't touch CRM operations, pipeline management, or revenue intelligence. You still do all the CRM work manually.</p>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/30">
              <h3 className="text-xl font-bold text-primary mb-4">NoQ5 = Autonomous CRM Execution</h3>
              <p className="text-muted-foreground">NoQ5 is an AI agent that runs your CRM end-to-end. Data entry, enrichment, pipeline management, forecasting, and meeting intelligence — all automated. Apollo fills your pipeline; NoQ5 manages it.</p>
            </div>
          </div>
        </div>
      </section>

      {/* When to Choose */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">When to Choose What</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card p-8 rounded-xl border border-border">
              <h3 className="text-xl font-bold mb-4">Choose Apollo.io if:</h3>
              <ul className="space-y-2">
                {["You need a contact database for cold outreach", "Email sequences are your primary channel", "You don't need CRM automation", "You have a dedicated team for CRM admin"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><span className="text-muted-foreground">•</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/30">
              <h3 className="text-xl font-bold text-primary mb-4">Choose NoQ5 if:</h3>
              <ul className="space-y-2">
                {["You want to eliminate CRM data entry", "Pipeline accuracy matters to your business", "You need AI-powered revenue forecasting", "GDPR compliance is required", "You want one AI agent managing all CRM work"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-1" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">Ready to Go Beyond Prospecting?</h2>
          <p className="text-xl opacity-90 mb-8">Apollo fills your pipeline. NoQ5 runs it. Try the AI CRM agent that replaces manual work completely.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ApolloVsNoQ5;
